import { LayoutGridIcon, MapIcon, SearchIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/web/components/primitives/select";
import { ToggleGroup, ToggleGroupItem } from "@/web/components/primitives/toggle-group";
import {
  ALL_BUSINESS_CATEGORIES,
  ALL_CANONICAL_BUSINESS_TAGS,
  BUSINESS_CATEGORIES_AND_TYPES,
  getTagMetadata,
} from "@/web/lib/schemaUtils/businessCategories";
import { cn } from "@/web/lib/styling";
import { MultiSelect } from "@/web/routes/browse/-components/MultiSelect";
import { SearchInput } from "@/web/routes/browse/-components/SearchInput";
import type { BrowseSort } from "@/web/routes/browse/-components/types";
import { useCampaignBrowserSearch } from "@/web/routes/browse/-components/useBrowse";

export type BrowseViewMode = "grid" | "map";

interface BrowseFiltersBarProps {
  className?: string;
  onSearchClick: () => void;
  searchExpanded?: boolean;
  searchText?: string;
  onClearSearch?: () => void;
  onSearchChange?: (value: string) => void;
  viewMode: BrowseViewMode;
  onViewModeChange: (mode: BrowseViewMode) => void;
}

export function BrowseFiltersBar({
  className,
  onSearchClick,
  searchExpanded,
  searchText,
  onClearSearch,
  onSearchChange,
  viewMode,
  onViewModeChange,
}: BrowseFiltersBarProps) {
  const { search, toggleCategory, toggleTag, setSort } = useCampaignBrowserSearch();
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const industryOptions = ALL_BUSINESS_CATEGORIES.map((category) => {
    const categoryDef = BUSINESS_CATEGORIES_AND_TYPES[category];
    const Icon = categoryDef.icon;
    return {
      value: category,
      label: categoryDef.friendlyName,
      icon: <Icon className="size-4.5" />,
    };
  });

  const communityOptions = ALL_CANONICAL_BUSINESS_TAGS.map((tag) => ({
    value: tag,
    label: getTagMetadata(tag).friendlyName,
  }));

  useEffect(() => {
    if (searchExpanded && searchInputRef.current && !searchText) {
      searchInputRef.current.focus();
    }
  }, [searchExpanded, searchText]);

  const handleShowInput = useCallback(() => {
    onSearchClick();
    setIsSearchFocused(true);
  }, [onSearchClick]);

  const handleUsedSearchClick = useCallback(() => {
    setIsSearchFocused(true);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  }, []);

  const showInput = isSearchFocused || !searchText;

  const sortSelect = (
    <Select value={search.sort ?? "recent"} onValueChange={(value) => setSort(value as BrowseSort)}>
      <SelectTrigger
        className="font-withus-sans h-11 w-40 shrink-0 text-base font-medium"
        aria-label="Sort campaigns"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="recent">Most recent</SelectItem>
        <SelectItem value="alphabetical">A → Z</SelectItem>
        <SelectItem value="alphabetical-desc">Z → A</SelectItem>
      </SelectContent>
    </Select>
  );

  const viewToggle = (
    <ToggleGroup
      type="single"
      value={viewMode}
      onValueChange={(v) => v !== "" && onViewModeChange(v as BrowseViewMode)}
      className="h-11 rounded-none border border-border "
    >
      <ToggleGroupItem value="grid" aria-label="Grid view" className="h-full">
        <LayoutGridIcon className="mx-1 size-4" />
      </ToggleGroupItem>
      <ToggleGroupItem value="map" aria-label="Map view" className="h-full">
        <MapIcon className="mx-1 size-4" />
      </ToggleGroupItem>
    </ToggleGroup>
  );

  const sortAndView = (
    <div className="flex shrink-0 items-center gap-3">
      {sortSelect}
      {viewToggle}
    </div>
  );

  return (
    <div className={cn("md:flex md:items-center md:justify-between", className)}>
      <div className="flex flex-wrap items-stretch gap-3">
        <MultiSelect
          options={industryOptions}
          selectedValues={search.categories ?? []}
          onToggle={(category) => toggleCategory(category)}
          placeholder="Industries"
        />

        <MultiSelect
          options={communityOptions}
          selectedValues={search.tags ?? []}
          onToggle={(tag) => toggleTag(tag)}
          placeholder="Communities"
        />

        {showInput ? (
          searchExpanded ? (
            <SearchInput
              ref={searchInputRef}
              value={search.fullText ?? ""}
              onChange={(value) => onSearchChange?.(value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
            />
          ) : (
            <button
              type="button"
              onClick={handleShowInput}
              className={cn(
                "inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-none border border-border",
                "bg-white p-3 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.08)]",
                "focus-visible:ring-2 focus-visible:ring-black/20 focus-visible:outline-none",
              )}
              aria-label="Search"
            >
              <SearchIcon className="size-4 shrink-0 text-withus-dark-blue" />
            </button>
          )
        ) : (
          <div
            className={cn(
              "inline-flex h-11 min-w-60 shrink-0 items-center gap-2.5 rounded-none border border-withus-blue bg-white px-3.5 py-3",
            )}
          >
            <SearchIcon className="size-4 shrink-0 text-withus-dark-blue" />
            <button
              type="button"
              onClick={handleUsedSearchClick}
              className="flex-1 truncate text-left text-[16px] font-medium text-withus-blue"
            >
              {searchText}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClearSearch?.();
                setIsSearchFocused(false);
              }}
              className="shrink-0"
              aria-label="Clear search"
            >
              <XIcon className="size-4 text-withus-dark-blue" />
            </button>
          </div>
        )}

        <div className="md:hidden">{sortAndView}</div>
      </div>

      <div className="hidden md:block">{sortAndView}</div>
    </div>
  );
}
